import { Card, Input, InputNumber, Select, Table } from "antd";
import { get, isNil } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { NAME_SPACES } from "shared/locales/constants";
import { SCREENS } from "utils/constants";
import {
  collectiveSections,
  constantEntries,
  CONSTANTS_KEY,
  fromPercent,
  gridBrackets,
  gridCategories,
  gridCellPath,
  gridLevelCount,
  gridMembers,
  gridStructures,
  gridZones,
  hasRegimeColumns,
  orphanGridKeys,
  planBrackets,
  planColumns,
  reinforcementCellPath,
  reinforcementCodes,
  reinforcementRows,
  toPercent,
  usesPlanAxes,
  withValue,
  zoneChoices,
  ZONIER_KEY,
  zonierRows,
} from "./grid";

const RATE_STEP = 0.001;
const ZONIER_PAGE_SIZE = 15;

const labelOf = (dictionary, key) => get(dictionary, key, key);

const RateInput = ({ prices, path, onChange }) => (
  <InputNumber
    size="small"
    className="collective-pricer__rate"
    step={RATE_STEP}
    min={0}
    value={toPercent(get(prices, path))}
    onChange={(value) => {
      if (isNil(value)) return;
      onChange(withValue(prices, path, fromPercent(value)));
    }}
  />
);

const Constants = ({ prices, onChange, labels }) => (
  <div className="collective-pricer__constants">
    {constantEntries(prices).map(([key, value]) => (
      <label key={key} className="collective-pricer__constant">
        <span>{labelOf(labels, key)}</span>
        <InputNumber
          value={value}
          onChange={(next) => {
            if (isNil(next)) return;
            onChange(withValue(prices, [CONSTANTS_KEY, key], next));
          }}
        />
      </label>
    ))}
  </div>
);

const Grid = ({ prices, onChange, COLLECTIVE, axes }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const structures = gridStructures(prices);
  const [selectedStructure, setStructure] = useState(structures[0]);
  const structure = structures.includes(selectedStructure)
    ? selectedStructure
    : structures[0];
  const categories = gridCategories(prices, structure);
  const [selectedCategory, setCategory] = useState(categories[0]);
  const category = categories.includes(selectedCategory)
    ? selectedCategory
    : categories[0];

  const planDriven = usesPlanAxes(axes);
  const zones = planDriven
    ? planColumns(axes)
    : gridZones(prices, structure, category).map((zone) => ({
        key: zone,
        label: labelOf(COLLECTIVE.ZONES, zone),
      }));
  const brackets = planDriven
    ? planBrackets(axes)
    : gridBrackets(prices, structure, category);
  const orphans = planDriven
    ? orphanGridKeys(prices, structure, category, axes)
    : [];
  const members = gridMembers(prices, structure, category);
  const levelCount = gridLevelCount(prices, structure, category);
  const levelIndexes = Array.from({ length: levelCount }, (_, index) => index);

  const cell = (zone, member, levelIndex) => ({
    key: [zone, member, levelIndex].join("."),
    title: isNil(levelIndex)
      ? labelOf(COLLECTIVE.MEMBERS, member)
      : `${COLLECTIVE.LEVEL_SHORT}${levelIndex + 1}`,
    render: (_, { bracket }) => (
      <RateInput
        prices={prices}
        onChange={onChange}
        path={gridCellPath({
          structure,
          category,
          zone,
          bracket,
          member,
          levelIndex,
        })}
      />
    ),
  });

  const columns = [
    {
      key: "bracket",
      title: COLLECTIVE.BRACKET,
      dataIndex: "bracket",
      fixed: "left",
    },
    ...zones.map(({ key: zone, label }) => ({
      key: zone,
      title: label,
      children: members.map((member) =>
        levelCount === 0
          ? cell(zone, member)
          : {
              key: `${zone}.${member}`,
              title: labelOf(COLLECTIVE.MEMBERS, member),
              children: levelIndexes.map((levelIndex) =>
                cell(zone, member, levelIndex),
              ),
            },
      ),
    })),
  ];

  return (
    <>
      <div className="d-flex gap-16 flex--wrap m-bottom-16">
        <label className="collective-pricer__axis">
          <span>{COLLECTIVE.STRUCTURE}</span>
          <Select
            value={structure}
            onChange={setStructure}
            options={structures.map((value) => ({
              value,
              label: labelOf(COLLECTIVE.STRUCTURES, value),
            }))}
          />
        </label>
        <label className="collective-pricer__axis">
          <span>{COLLECTIVE.CATEGORY}</span>
          <Select
            value={category}
            onChange={setCategory}
            options={categories.map((value) => ({
              value,
              label: labelOf(COLLECTIVE.CATEGORIES, value),
            }))}
          />
        </label>
      </div>
      {orphans.length > 0 && (
        <p className="collective-pricer__hint collective-pricer__hint--warning m-bottom-16">
          {t("COLLECTIVE.ORPHAN_KEYS", { keys: orphans.join(", ") })}
        </p>
      )}
      <Table
        size="small"
        bordered
        pagination={false}
        scroll={{ x: true }}
        rowKey="bracket"
        columns={columns}
        dataSource={brackets.map((bracket) => ({ bracket }))}
      />
    </>
  );
};

const PlanAxesNotice = ({ axes, planId, COLLECTIVE }) => (
  <div className="collective-pricer__notice">
    <p>{COLLECTIVE.PLAN_AXES_HINT}</p>
    {hasRegimeColumns(axes) && <p>{COLLECTIVE.REGIME_COLUMN_HINT}</p>}
    {planId && (
      <Link
        to={`${SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.CONFIGURE.path}/${planId}`}
      >
        {COLLECTIVE.PLAN_AXES_LINK}
      </Link>
    )}
  </div>
);

const Reinforcements = ({ prices, onChange, COLLECTIVE }) => {
  const codes = reinforcementCodes(prices);
  const rateColumns =
    codes.length === 0
      ? [
          {
            key: "rate",
            title: COLLECTIVE.RATE,
            render: (_, row) => (
              <RateInput
                prices={prices}
                onChange={onChange}
                path={reinforcementCellPath(row)}
              />
            ),
          },
        ]
      : codes.map((code) => ({
          key: code,
          title: labelOf(COLLECTIVE.REINFORCEMENTS, code),
          render: (_, row) => (
            <RateInput
              prices={prices}
              onChange={onChange}
              path={reinforcementCellPath({ ...row, code })}
            />
          ),
        }));

  return (
    <Table
      size="small"
      bordered
      pagination={false}
      scroll={{ x: true }}
      rowKey={({ structure, member }) => `${structure}.${member}`}
      dataSource={reinforcementRows(prices)}
      columns={[
        {
          key: "structure",
          title: COLLECTIVE.STRUCTURE,
          dataIndex: "structure",
          render: (value) => labelOf(COLLECTIVE.STRUCTURES, value),
        },
        {
          key: "member",
          title: COLLECTIVE.MEMBER,
          dataIndex: "member",
          render: (value) => labelOf(COLLECTIVE.MEMBERS, value),
        },
        ...rateColumns,
      ]}
    />
  );
};

const Zonier = ({ prices, onChange, COLLECTIVE }) => {
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const rows = zonierRows(prices).filter(
    ({ department, name }) =>
      !needle ||
      department.toLowerCase().includes(needle) ||
      name.toLowerCase().includes(needle),
  );
  const zones = zoneChoices(prices).map((value) => ({
    value,
    label: labelOf(COLLECTIVE.ZONES, value),
  }));

  return (
    <>
      <Input
        allowClear
        className="collective-pricer__search m-bottom-16"
        placeholder={COLLECTIVE.ZONIER_SEARCH}
        value={search}
        onChange={({ target }) => setSearch(target.value)}
      />
      <Table
        size="small"
        rowKey="department"
        pagination={{ pageSize: ZONIER_PAGE_SIZE, showSizeChanger: false }}
        dataSource={rows}
        columns={[
          {
            key: "department",
            title: COLLECTIVE.DEPARTMENT,
            dataIndex: "department",
          },
          { key: "name", title: COLLECTIVE.DEPARTMENT_NAME, dataIndex: "name" },
          {
            key: "zone",
            title: COLLECTIVE.ZONE,
            render: (_, { department, zone }) => (
              <Select
                size="small"
                className="collective-pricer__zone"
                value={zone}
                options={zones}
                onChange={(value) =>
                  onChange(
                    withValue(prices, [ZONIER_KEY, department, "zone"], value),
                  )
                }
              />
            ),
          },
        ]}
      />
    </>
  );
};

const CollectivePricerEditor = ({ prices, onChange, axes, planId }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const COLLECTIVE = t("COLLECTIVE", { returnObjects: true });
  const sections = collectiveSections(prices);
  const sectionProps = { prices, onChange, COLLECTIVE, axes };

  return (
    <div className="collective-pricer d-flex flex--column gap-16">
      <p className="collective-pricer__hint">{COLLECTIVE.RATE_UNIT_HINT}</p>
      {sections.grid && usesPlanAxes(axes) && (
        <PlanAxesNotice axes={axes} planId={planId} COLLECTIVE={COLLECTIVE} />
      )}
      {sections.constants && (
        <Card size="small" title={COLLECTIVE.CONSTANTS_TITLE}>
          <Constants {...sectionProps} labels={COLLECTIVE.CONSTANTS} />
        </Card>
      )}
      {sections.grid && (
        <Card size="small" title={COLLECTIVE.GRID_TITLE}>
          <Grid {...sectionProps} />
        </Card>
      )}
      {sections.reinforcements && (
        <Card size="small" title={COLLECTIVE.REINFORCEMENTS_TITLE}>
          <Reinforcements {...sectionProps} />
        </Card>
      )}
      {sections.zonier && (
        <Card size="small" title={COLLECTIVE.ZONIER_TITLE}>
          <Zonier {...sectionProps} />
        </Card>
      )}
    </div>
  );
};

export default CollectivePricerEditor;
